/**
 * The tenant's own page shell around a BYOD page's sections.
 *
 * The root owns header, footer, page background and content width. Until now
 * it shaped only the editor canvas: the published page kept the skeleton's
 * chrome, so every root prop an author set was invisible to visitors.
 *
 * Applied ONLY when this page actually uses tenant components and the tenant
 * ships a root. A page of built-in sections keeps the skeleton's chrome, so
 * nothing changes for tenants without a page-builder registry.
 *
 * `logoHref` is injected the same way: where the header logo links comes from
 * the page an editor flagged as home in Studio, not from a constant in each
 * tenant package, so moving the flag does not need a tenant redeploy. It is a
 * DEFAULT — a root that declares its own `logoHref` field keeps whatever the
 * author set there, because a page may deliberately point its logo elsewhere.
 *
 * `localeSwitcher` is injected rather than authored — it is absent from the
 * registry's fields and from the manifest, the same convention as
 * ProductGrid's `products`. It exists because the skeleton's navbar hosts the
 * language switcher, and replacing that navbar with this root would otherwise
 * lose it on precisely the pages a tenant designed itself.
 */
import { registry as tenantRegistry } from '@crosure/tenant/pageBuilder'
import type { TenantComponentRegistry } from '@crosure/tenant-runtime'
import { useTheme } from '@chakra-ui/react'
import React from 'react'
import {
	COLOR_MODE_ATTRIBUTE,
	formDarkPaletteCss,
} from '@/lib/formDarkPalette'

const registry = tenantRegistry as TenantComponentRegistry | null

interface SchemaSection {
	section_type: string
}

/**
 * Should this page be wrapped in the tenant's root?
 *
 * Both conditions matter. No tenant components means the page is built from
 * skeleton sections and should keep skeleton chrome; no root means the tenant
 * ships components but no shell, and wrapping in nothing would just remove
 * the header.
 */
export function usesTenantRoot(sections: SchemaSection[]): boolean {
	if (!hasTenantRoot()) return false
	return sections.some(section => section.section_type === 'tenant_component')
}

/** Does this build's tenant package ship a page root at all? */
export function hasTenantRoot(): boolean {
	return Boolean(registry?.root?.render)
}

/** Studio's `logoHref` default, injected UNDER the authored props so an
 *  explicit one wins. */
function withHomeHref(
	props: Record<string, unknown>,
	homeHref?: string
): Record<string, unknown> {
	return homeHref && !props.logoHref ? { ...props, logoHref: homeHref } : props
}

interface Props {
	sections: SchemaSection[]
	rootProps?: Record<string, unknown> | null
	localeSwitcher?: React.ReactNode
	/** Studio's answer for where the header logo links. */
	homeHref?: string
	children: React.ReactNode
}

export function TenantPageRoot({
	sections,
	rootProps,
	localeSwitcher,
	homeHref,
	children,
}: Props) {
	if (!usesTenantRoot(sections)) return <>{children}</>

	const Root = registry?.root?.render as React.ComponentType<
		Record<string, unknown>
	>

	return (
		<Root
			{...withHomeHref(rootProps ?? {}, homeHref)}
			localeSwitcher={localeSwitcher}
		>
			{children}
		</Root>
	)
}

interface ShellProps {
	/** Studio's page-shell props (GET /api/client/v1/page-shell/). */
	rootProps: Record<string, unknown>
	homeHref?: string
	/** Injected, as on a BYOD page: the navbar this replaces hosted it. */
	localeSwitcher?: React.ReactNode
	children: React.ReactNode
}

/**
 * The tenant root around a page that is NOT a Studio page — the form journey.
 *
 * Wrapped around the Layout's scroll container rather than the page content,
 * so the header sits exactly where the skeleton navbar did and the form's own
 * layout (full-height split image, sticky progress bar) is untouched.
 *
 * `surface="form"` is injected, not authored — the same convention as
 * `localeSwitcher`. It tells the root to drop its footer and to mirror its
 * colour mode onto <html> as `data-crosure-color-mode`, which switches the
 * form engine's palette (see lib/formDarkPalette.ts). Removed on unmount so
 * leaving the form can never leave a dashboard dark.
 */
export function TenantShell({
	rootProps,
	homeHref,
	localeSwitcher,
	children,
}: ShellProps) {
	const theme = useTheme()
	const darkCss = React.useMemo(
		() => formDarkPaletteCss(theme.colors ?? {}),
		[theme.colors]
	)

	React.useEffect(
		() => () => document.documentElement.removeAttribute(COLOR_MODE_ATTRIBUTE),
		[]
	)

	if (!hasTenantRoot()) return <>{children}</>
	const Root = registry?.root?.render as React.ComponentType<
		Record<string, unknown>
	>

	return (
		<>
			{/* Generated from the active Chakra theme — no authored value is
			    interpolated, so there is no injection surface. */}
			<style
				id='crosure-form-dark-palette'
				dangerouslySetInnerHTML={{ __html: darkCss }}
			/>
			<Root
				{...withHomeHref(rootProps, homeHref)}
				surface='form'
				localeSwitcher={localeSwitcher}
			>
				{children}
			</Root>
		</>
	)
}

export default TenantPageRoot
